import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload as UploadIcon, Video, Radio, CheckCircle, Clock, XCircle, Trash2, Loader2, User, FolderOpen, MoreVertical,
  Pencil, PlusCircle, Layers, ArrowLeft, Image as ImageIcon, Link2, Search, LayoutGrid, List as ListIcon,
  Archive, ArchiveRestore, Cloud, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getFolders, createFolder as createFolderRecord, getFolderAssetIds, getFolderMembers, addAssetToFolder, removeAssetFromFolder,
  getArchivedAssetIds, setAssetArchived, getArchivedProjectIds, setProjectArchived, getArchivedFolderIds, setFolderArchived,
  getLocalDesignAssets, getDisplayNameOverrides, setDisplayNameOverride,
} from '@/lib/mediaLibrary';
import { MediaLibrarySidebar } from '@/components/media-library/MediaLibrarySidebar';
import { AssetGridCard } from '@/components/media-library/AssetGridCard';
import { AssetPreviewDialog } from '@/components/media-library/AssetPreviewDialog';
import { DeleteAssetDialog } from '@/components/media-library/DeleteAssetDialog';
import { DropboxBackupDialog } from '@/components/media-library/DropboxBackupDialog';
import { BackupSettingsPanel } from '@/components/media-library/BackupSettingsPanel';
import { CreateFolderDialog } from '@/components/media-library/CreateFolderDialog';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API_URL = window.__BACKEND_URL;

const mapSourceToAsset = (source) => {
  const metadata = source.metadata_json || {};
  const rawStatus = metadata.demo_status || source.status || 'processing';
  const status = rawStatus;
  return {
    id: source.id,
    name: source.title || source.input_file_url?.split('/').pop() || 'untitled.mp4',
    type: source.type || 'upload',
    createdAt: source.created_at || null,
    status,
    progress: metadata.progress ?? (status === 'processing' ? 50 : 100),
    isDemo: !!metadata.is_demo,
    parent_asset_id: source.parent_asset_id || null,
    project_id: source.project_id || null,
    clean_status: source.clean_status || null,
    clean_stage: source.clean_stage || null,
    clean_progress: source.clean_progress || 0,
    clean_stats: source.clean_stats || null,
    clean_error: source.clean_error || null,
    clean_message: source.clean_message || null,
    clean_updated_at: source.updated_at || null,
    approved_for_clips: source.approved_for_clips || false,
  };
};

export default function ContentStudio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Optional project context — arriving from Project Overview / Project
  // Upload / Editor / Publish so the shared Media Library can default to
  // showing that project's media and let the user get back to where they
  // came from, without this ever becoming a second Media Library.
  const projectId = searchParams.get('projectId');
  const returnTo = searchParams.get('returnTo');
  const attachMode = searchParams.get('mode') === 'attach';
  // Media Library navigation — Creator → Projects → Folders → Assets.
  // 'all' | 'project' | 'folder' | 'recent' | 'archived' | 'backups'
  const [libraryView, setLibraryView] = useState(projectId ? 'project' : 'all');
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [archivedIds, setArchivedIds] = useState(() => new Set());
  const [archivedProjectIds, setArchivedProjectIds] = useState(() => new Set());
  const [archivedFolderIds, setArchivedFolderIds] = useState(() => new Set());
  const [localDesignAssets, setLocalDesignAssets] = useState([]);
  const [displayNameOverrides, setDisplayNameOverrides] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [previewAsset, setPreviewAsset] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupSelection, setBackupSelection] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [creatorId, setCreatorId] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const fileInputRef = useRef(null);
  const finalizingRef = useRef(new Set());

  // Backblaze B2 is the real, already-working primary storage for every
  // upload (see backend `b2_storage_service` used by the chunked upload
  // pipeline below) — this health check only ever reports the OPTIONAL
  // Dropbox backup connection, never primary storage status.
  const [dropboxConnected, setDropboxConnected] = useState(false);
  const [storageNotice, setStorageNotice] = useState('Checking Dropbox backup connection…');

  useEffect(() => {
    const checkStorage = async () => {
      try {
        const healthRes = await fetch(`${API_URL}/api/storage/health`, { credentials: 'include' });
        if (healthRes.ok) {
          const health = await healthRes.json();
          if (health.connected) {
            setDropboxConnected(true);
            setStorageNotice(`Dropbox backup connected${health.account_email ? ` (${health.account_email})` : ''}.`);
            return;
          }
        }
        setDropboxConnected(false);
        setStorageNotice('Dropbox backup not connected — your media is still safely stored in Backblaze B2.');
      } catch {
        setDropboxConnected(false);
        setStorageNotice('Could not check Dropbox backup connection — your media is still safely stored in Backblaze B2.');
      }
    };
    checkStorage();
  }, []);

  useEffect(() => {
    const fetchCreatorProfile = async () => {
      try {
        const activeId = localStorage.getItem('activeCreatorId');

        if (activeId) {
          // Respect the specific creator selected in Creator Hub — the
          // generic /api/creator-profile endpoint returns an arbitrary
          // single profile for the user and ignores which creator was
          // actually selected when the user manages more than one.
          const listRes = await fetch(`${API_URL}/api/creators`, { credentials: 'include' });
          if (listRes.ok) {
            const list = await listRes.json();
            const match = (Array.isArray(list) ? list : []).find(c => (c.id || c._id) === activeId);
            if (match) {
              setCreatorId(match.id || match._id);
              if (match.brand_id) setBrandId(match.brand_id);
              setCreatorInfo(match);
              return;
            }
            // activeCreatorId is stale/unauthorized for this account — don't
            // silently fall through to a DIFFERENT creator's workspace.
            localStorage.removeItem('activeCreatorId');
            toast.error('That creator is no longer available — pick a creator to continue');
            navigate('/app/create/hub');
            return;
          }
        }

        // Fallback: no creator selected (direct navigation) — use the
        // caller's default/first creator profile, preserving prior behavior.
        const res = await fetch(`${API_URL}/api/creator-profile`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setCreatorId(data.id || data.creator_id);
          if (data.brand_id) setBrandId(data.brand_id);
          setCreatorInfo(data);
        }
      } catch (err) {
        console.error('Failed to fetch creator profile:', err);
      }
    };
    fetchCreatorProfile();
  }, []);

  // Load existing assets for the active creator
  const fetchExistingAssets = useCallback(async () => {
    if (!creatorId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/content-sources?creator_id=${creatorId}`, { credentials: 'include' });
      if (!res.ok) return;
      const sources = await res.json();
      const mapped = (Array.isArray(sources) ? sources : [])
        .filter(s => ['upload', 'live', 'replay'].includes(s.type))
        .map(mapSourceToAsset);
      setAssets(mapped);
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  }, [creatorId]);

  useEffect(() => {
    fetchExistingAssets();
  }, [fetchExistingAssets]);

  // ── Media Library scaffolding: creator's projects, frontend-only folders,
  // archive state, and the browser-local design assets (thumbnails, image
  // posts, carousels, stories) that Images & Posts stores per-project with
  // no server persistence today. These never duplicate a backend record —
  // they are read fresh from the same local storage the editors already use.
  const loadLocalLibraryState = useCallback(async () => {
    if (!creatorId) return;
    setFolders(getFolders(creatorId));
    setArchivedIds(getArchivedAssetIds(creatorId));
    setArchivedProjectIds(getArchivedProjectIds(creatorId));
    setArchivedFolderIds(getArchivedFolderIds(creatorId));
    setDisplayNameOverrides(getDisplayNameOverrides(creatorId));
    try {
      const res = await fetch(`${API_URL}/api/projects?creator_id=${creatorId}`, { credentials: 'include' });
      const list = res.ok ? await res.json() : [];
      const projectList = Array.isArray(list) ? list : (list.projects || []);
      setProjects(projectList);
      const designs = projectList.flatMap((p) => getLocalDesignAssets(p.id || p._id, p.name || p.title));
      setLocalDesignAssets(designs);
    } catch (err) {
      console.error('Failed to load projects/local design assets:', err);
    }
  }, [creatorId]);

  useEffect(() => {
    loadLocalLibraryState();
  }, [loadLocalLibraryState]);

  // ── Chunked upload machinery ──
  const CHUNK_SIZE = 5 * 1024 * 1024;
  const CONCURRENCY = /Mobi|Android/i.test(navigator.userAgent) ? 4 : 6;
  const MAX_RETRIES = 3;
  const UPLOAD_LS_PREFIX = 'cs_upload_';

  // ── localStorage helpers for resumable uploads ──
  const makeFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
  const getUploadSession = (fileKey) => {
    try { return JSON.parse(localStorage.getItem(UPLOAD_LS_PREFIX + fileKey)); } catch { return null; }
  };
  const saveUploadSession = (fileKey, data) => {
    try { localStorage.setItem(UPLOAD_LS_PREFIX + fileKey, JSON.stringify(data)); } catch {}
  };
  const clearUploadSession = (fileKey) => {
    try { localStorage.removeItem(UPLOAD_LS_PREFIX + fileKey); } catch {}
  };

  const sendRequest = useCallback((url, body, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;
      xhr.timeout = 120000;
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
        } else {
          let detail = 'Request failed';
          let errorCode = null;
          try {
            const parsed = JSON.parse(xhr.responseText);
            const parsedDetail = parsed?.detail;
            if (typeof parsedDetail === 'string') detail = parsedDetail;
            else if (parsedDetail && typeof parsedDetail === 'object') {
              detail = parsedDetail.message || JSON.stringify(parsedDetail);
              errorCode = parsedDetail.error_code || null;
            }
          } catch {}
          const err = new Error(detail);
          err.statusCode = xhr.status;
          err.errorCode = errorCode;
          reject(err);
        }
      });
      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('timeout', () => reject(new Error('Request timed out')));
      xhr.open(options.method || 'POST', url);
      if (options.headers) Object.entries(options.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.send(body);
    });
  }, []);

  const sendChunkWithRetry = useCallback(async (uploadId, chunkIndex, blob, retries = MAX_RETRIES) => {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('chunk_index', chunkIndex);
        form.append('file', blob, `chunk_${chunkIndex}`);
        return await sendRequest(`${API_URL}/api/upload/chunk`, form);
      } catch (err) {
        if (attempt === retries - 1) throw err;
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
      }
    }
  }, [sendRequest]);

  // ── 409 Recovery: check server truth for a given uploadId ──
  const recoverFromFinalize = async (uploadId) => {
    try {
      const statusResp = await fetch(`${API_URL}/api/upload/status/${uploadId}`, { credentials: 'include' });
      if (!statusResp.ok) return null;
      const session = await statusResp.json();
      console.log('[Upload] Recovery session check:', session);
      if (session.status === 'completed' && session.content_source_id) {
        const assetResp = await fetch(`${API_URL}/api/admin/content-sources`, { credentials: 'include' });
        if (!assetResp.ok) return null;
        const sources = await assetResp.json();
        return sources.find(s => s.id === session.content_source_id) || null;
      }
    } catch (e) { console.log('[Upload] Recovery failed:', e.message); }
    return null;
  };

  const uploadFileWithProgress = async (file, parentAssetId = null) => {
    const fileKey = makeFileKey(file);
    const tempId = `uploading_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    setAssets(prev => [{ id: tempId, name: file.name, status: 'uploading', progress: 0, fileSize: file.size, isDemo: false, parent_asset_id: parentAssetId }, ...prev]);

    let lastProgressUpdate = 0;
    let completedChunks = 0;
    const completedSet = new Set();
    const updateProgress = (force = false) => {
      const now = Date.now();
      if (!force && now - lastProgressUpdate < 250) return;
      lastProgressUpdate = now;
      const pct = Math.round((completedChunks / totalChunks) * 100);
      setAssets(prev => prev.map(a => a.id === tempId ? { ...a, progress: pct } : a));
    };

    let uploadId = null;

    try {
      // ── Step 1: Resume or init a new session ──
      const existing = getUploadSession(fileKey);
      let resuming = false;

      if (existing?.uploadId) {
        // Validate existing session on server
        try {
          const sResp = await fetch(`${API_URL}/api/upload/status/${existing.uploadId}`, { credentials: 'include' });
          if (sResp.ok) {
            const sData = await sResp.json();
            if (sData.status === 'completed') {
              // Already finished — recover the asset
              console.log('[Upload] Session already completed, recovering...', existing.uploadId);
              const recovered = await recoverFromFinalize(existing.uploadId);
              if (recovered) {
                clearUploadSession(fileKey);
                const newAsset = mapSourceToAsset(recovered);
                setAssets(prev => prev.map(a => a.id === tempId ? newAsset : a));
                return recovered;
              }
            }
            if (sData.status === 'in_progress' && sData.total_chunks === totalChunks) {
              // Valid in-progress session — resume
              uploadId = existing.uploadId;
              resuming = true;
              (sData.completed_chunks || []).forEach(i => completedSet.add(i));
              completedChunks = completedSet.size;
              console.log('[Upload] Resuming session:', { uploadId, completedChunks, totalChunks });
              updateProgress(true);
            }
          }
        } catch { /* server unreachable, start fresh */ }
      }

      if (!uploadId) {
        // Start a fresh session — clear any stale data for this fileKey
        clearUploadSession(fileKey);

        const initForm = new FormData();
        initForm.append('filename', file.name);
        initForm.append('total_chunks', totalChunks);
        initForm.append('total_size', file.size);
        initForm.append('creator_id', creatorId);
        if (brandId) initForm.append('brand_id', brandId);
        initForm.append('title', file.name);
        initForm.append('description', 'Uploaded from Content Studio');
        initForm.append('content_type', 'long_form');
        if (parentAssetId) initForm.append('parent_asset_id', parentAssetId);

        const initResp = await sendRequest(`${API_URL}/api/upload/init`, initForm);
        uploadId = initResp.upload_id;
        if (!uploadId) throw new Error('Failed to initialize upload');
        console.log('[Upload] New session:', { uploadId, fileKey, totalChunks });
      }

      // Persist session to localStorage
      saveUploadSession(fileKey, { uploadId, fileKey, totalChunks, filename: file.name, fileSize: file.size });

      // ── Step 2: Upload chunks (skip already-completed) ──
      const pendingChunks = [];
      for (let i = 0; i < totalChunks; i++) {
        if (!completedSet.has(i)) pendingChunks.push(i);
      }
      if (resuming && pendingChunks.length < totalChunks) {
        console.log('[Upload] Skipping', totalChunks - pendingChunks.length, 'already-uploaded chunks');
      }

      let cursor = 0;
      const uploadNext = async () => {
        while (cursor < pendingChunks.length) {
          const ci = cursor++;
          const idx = pendingChunks[ci];
          const start = idx * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);
          await sendChunkWithRetry(uploadId, idx, blob);
          completedSet.add(idx);
          completedChunks = completedSet.size;
          updateProgress();
        }
      };

      const workers = Array.from({ length: Math.min(CONCURRENCY, pendingChunks.length || 1) }, () => uploadNext());
      await Promise.all(workers);
      completedChunks = totalChunks;
      updateProgress(true);

      // ── Step 3: Wait for server to confirm all chunks ──
      const fetchStatus = async () => {
        const resp = await fetch(`${API_URL}/api/upload/status/${uploadId}`, { credentials: 'include' });
        if (!resp.ok) throw new Error('Failed to check upload status');
        return resp.json();
      };
      const uploadMissing = async (missingIndices) => {
        for (const idx of missingIndices) {
          const start = idx * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          await sendChunkWithRetry(uploadId, idx, file.slice(start, end));
        }
      };
      const waitForServerComplete = async (maxAttempts = 10) => {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise(r => setTimeout(r, attempt === 0 ? 1000 : 2000 * Math.min(attempt, 3)));
          const status = await fetchStatus();
          if (status.chunks_received >= status.total_chunks) return true;
          const srvCompleted = new Set(status.completed_chunks || []);
          const missing = [];
          for (let i = 0; i < status.total_chunks; i++) { if (!srvCompleted.has(i)) missing.push(i); }
          if (missing.length > 0) await uploadMissing(missing);
        }
        return false;
      };

      const serverReady = await waitForServerComplete();
      if (!serverReady) throw new Error('Upload incomplete after multiple retries');

      // ── Step 4: Finalize (once, guarded) ──
      setAssets(prev => prev.map(a => a.id === tempId ? { ...a, status: 'finalizing', progress: 100, uploadId } : a));
      await new Promise(r => setTimeout(r, 500));

      if (finalizingRef.current.has(uploadId)) {
        throw new Error('Finalize already in progress');
      }
      finalizingRef.current.add(uploadId);

      console.log('[Upload] Calling finalize:', { fileKey, uploadId, filename: file.name, fileSize: file.size, totalChunks, completedChunks });

      let created = null;
      try {
        created = await sendRequest(`${API_URL}/api/upload/finalize`, JSON.stringify({ upload_id: uploadId }), { headers: { 'Content-Type': 'application/json' } });
        console.log('[Upload] Finalize success:', { id: created?.id, status: created?.status });
      } catch (finalizeErr) {
        console.log('[Upload] Finalize error:', { status: finalizeErr.statusCode, errorCode: finalizeErr.errorCode, message: finalizeErr.message });
        // Recovery: check server truth
        const recovered = await recoverFromFinalize(uploadId);
        if (recovered) {
          console.log('[Upload] Recovered asset after', finalizeErr.errorCode, ':', recovered.id);
          created = recovered;
        } else {
          throw finalizeErr;
        }
      } finally {
        finalizingRef.current.delete(uploadId);
      }

      // ── Step 5: Success — clear state, update UI ──
      clearUploadSession(fileKey);
      const newAsset = mapSourceToAsset(created);
      setAssets(prev => prev.map(a => a.id === tempId ? newAsset : a));

      return created;
    } catch (err) {
      const message = err?.message || 'Upload failed';
      const isFinalizeError = message.toLowerCase().includes('finalize') || message.toLowerCase().includes('missing chunk') || message.toLowerCase().includes('reassembly');
      setAssets(prev => prev.map(a =>
        a.id === tempId ? { ...a, status: isFinalizeError ? 'finalize_failed' : 'failed', progress: isFinalizeError ? 100 : 0, errorMessage: message, uploadId, fileKey } : a
      ));
      // Keep localStorage session so user can retry/resume — do NOT clear here
      throw err;
    }
  };

  const retryFinalize = async (asset) => {
    if (!asset.uploadId) return;
    if (finalizingRef.current.has(asset.uploadId)) {
      toast.error('Finalize already in progress');
      return;
    }
    finalizingRef.current.add(asset.uploadId);
    setAssets(prev => prev.map(a => a.id === asset.id ? { ...a, status: 'finalizing', errorMessage: null } : a));
    try {
      console.log('[Upload] Retry finalize:', { uploadId: asset.uploadId, assetName: asset.name });
      const created = await sendRequest(`${API_URL}/api/upload/finalize`, JSON.stringify({ upload_id: asset.uploadId }), { headers: { 'Content-Type': 'application/json' } });
      console.log('[Upload] Retry finalize success:', { id: created?.id });
      if (asset.fileKey) clearUploadSession(asset.fileKey);
      const newAsset = mapSourceToAsset(created);
      setAssets(prev => prev.map(a => a.id === asset.id ? newAsset : a));
      toast.success('Finalize succeeded');
    } catch (err) {
      console.log('[Upload] Retry finalize error:', { status: err.statusCode, errorCode: err.errorCode, message: err.message });
      // Recovery: check if asset was actually created server-side
      const recovered = await recoverFromFinalize(asset.uploadId);
      if (recovered) {
        console.log('[Upload] Retry recovery: found asset', recovered.id);
        if (asset.fileKey) clearUploadSession(asset.fileKey);
        const newAsset = mapSourceToAsset(recovered);
        setAssets(prev => prev.map(a => a.id === asset.id ? newAsset : a));
        toast.success('Upload completed (recovered)');
        return;
      }
      const message = err?.message || 'Finalize failed';
      setAssets(prev => prev.map(a => a.id === asset.id ? { ...a, status: 'finalize_failed', errorMessage: message } : a));
      toast.error(message);
    } finally {
      finalizingRef.current.delete(asset.uploadId);
    }
  };

  const handleUploadFiles = async (fileList, parentAssetId = null) => {
    if (!fileList?.length) return;
    if (!creatorId) {
      toast.error('Missing creator profile setup. Please complete profile setup first.');
      return;
    }
    const files = Array.from(fileList);
    setIsUploading(true);
    const results = await Promise.allSettled(files.map(f => uploadFileWithProgress(f, parentAssetId)));
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    if (succeeded > 0) toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded`);
    if (failed > 0) toast.error(`${failed} file${failed > 1 ? 's' : ''} failed to upload`);
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveAsset = (asset) => setDeleteTarget(asset);

  const performDeleteAsset = async (asset) => {
    if (asset.localOnly) {
      // Pure browser-local design record (thumbnail/image post/carousel/
      // Story) — removed directly from its project's local design list,
      // never touches the backend since no server record exists for it.
      try {
        const key = `consistency_images_posts_${asset.projectId}`;
        const list = JSON.parse(localStorage.getItem(key)) || [];
        localStorage.setItem(key, JSON.stringify(list.filter((d) => d.id !== asset.rawId)));
      } catch { /* storage unavailable */ }
      setLocalDesignAssets((current) => current.filter((d) => d.id !== asset.id));
      toast.success('Design removed from this browser');
      setDeleteTarget(null);
      return;
    }

    // Clear localStorage upload session if present
    if (asset.fileKey) clearUploadSession(asset.fileKey);

    // Optimistic UI removal
    const previousAssets = assets;
    setAssets(current => current.filter(a => a.id !== asset.id));
    setDeleteTarget(null);

    // Skip backend call for temp upload IDs (upload hasn't finalized yet)
    if (asset.id.startsWith('uploading_')) {
      toast.success('Upload cancelled');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/assets/${asset.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to remove asset'); }
      toast.success('Asset deleted');
    } catch (err) {
      setAssets(previousAssets);
      toast.error(err.message || 'Failed to remove asset');
    }
  };

  const handleArchiveToggle = (asset, archived) => {
    setAssetArchived(creatorId, asset.id, archived);
    setArchivedIds(getArchivedAssetIds(creatorId));
    toast.success(archived ? 'Moved to Archived (still safely stored, just hidden from the main library)' : 'Restored from Archived');
  };

  const handleRenameAsset = (asset, name) => {
    if (!name || !name.trim()) return;
    if (asset.localOnly) {
      try {
        const key = `consistency_images_posts_${asset.projectId}`;
        const list = JSON.parse(localStorage.getItem(key)) || [];
        localStorage.setItem(key, JSON.stringify(list.map((d) => (d.id === asset.rawId ? { ...d, name: name.trim() } : d))));
      } catch { /* storage unavailable */ }
      setLocalDesignAssets((current) => current.map((d) => (d.id === asset.id ? { ...d, name: name.trim() } : d)));
      toast.success('Design renamed');
      return;
    }
    setDisplayNameOverride(creatorId, asset.id, name.trim());
    setDisplayNameOverrides(getDisplayNameOverrides(creatorId));
    toast.success('Renamed in this Media Library (local label only — the original filename on the server is unchanged)');
  };

  const handleToggleFolderMembership = (asset, folderId, inFolder) => {
    if (inFolder) addAssetToFolder(creatorId, folderId, asset.id);
    else removeAssetFromFolder(creatorId, folderId, asset.id);
    toast.success(inFolder ? 'Added to folder' : 'Removed from folder');
  };

  const handleCreateFolder = (name) => {
    const { folders: next } = createFolderRecord(creatorId, { name, projectId: libraryView === 'project' ? selectedProjectId : null });
    setFolders(next);
    toast.success(`Folder "${name}" created`);
  };

  const handleArchiveProject = (projectId2, archived) => {
    setProjectArchived(creatorId, projectId2, archived);
    setArchivedProjectIds(getArchivedProjectIds(creatorId));
    if (archived && selectedProjectId === projectId2) { setLibraryView('all'); setSelectedProjectId(null); }
    toast.success(archived ? 'Project archived in this Media Library (project itself is unchanged)' : 'Project restored');
  };

  const handleArchiveFolder = (folderId, archived) => {
    setFolderArchived(creatorId, folderId, archived);
    setArchivedFolderIds(getArchivedFolderIds(creatorId));
    if (archived && selectedFolderId === folderId) { setLibraryView('all'); setSelectedFolderId(null); }
    toast.success(archived ? 'Folder archived' : 'Folder restored');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const getStatusBadge = (asset) => {
    const { status, progress } = asset;

    const config = {
      uploading: { icon: UploadIcon, color: 'bg-blue-500/10 text-blue-500 border-blue-500/30', label: `Uploading ${progress || 0}%` },
      finalizing: { icon: Loader2, color: 'bg-purple-500/10 text-purple-500 border-purple-500/30', label: 'Finalizing', spin: true },
      uploaded: { icon: CheckCircle, color: 'bg-green-500/10 text-green-500 border-green-500/30', label: 'Ready to Edit' },
      processing: { icon: Clock, color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30', label: 'Processing' },
      failed: { icon: XCircle, color: 'bg-red-500/10 text-red-500 border-red-500/30', label: 'Failed' },
      finalize_failed: { icon: XCircle, color: 'bg-red-500/10 text-red-500 border-red-500/30', label: 'Finalize Failed' },
    };
    const fallback = config.uploaded;
    const { icon: Icon, color, label, spin } = config[status] || fallback;
    return (
      <Badge className={`${color} border`} data-testid={`status-badge-${status}`}>
        <Icon className={`h-3 w-3 mr-1 ${spin ? 'animate-spin' : ''}`} />
        {label}
      </Badge>
    );
  };

  // Parent/Child Asset grouping — a content source with no parent_asset_id
  // IS the Parent Asset (one project); anything with parent_asset_id set
  // is a Child Asset (an additional video in that same project).
  const { parentAssets, childrenByParent } = useMemo(() => {
    const children = {};
    const parents = [];
    assets.forEach((a) => {
      if (a.parent_asset_id) {
        (children[a.parent_asset_id] = children[a.parent_asset_id] || []).push(a);
      } else {
        parents.push(a);
      }
    });
    return { parentAssets: parents, childrenByParent: children };
  }, [assets]);

  // Media Library navigation — Creator → Projects → Folders → Assets.
  // "Attach Existing Media" (attachMode) keeps its original, narrower
  // video-only semantics untouched: it always resolves against
  // `selectedProjectId`/`libraryView` the same way the old
  // projectId/showAllMedia toggle did, it just now lives in the sidebar.
  const scopedParentAssets = useMemo(() => {
    if (libraryView === 'folder' && selectedFolderId) {
      const ids = new Set(getFolderAssetIds(creatorId, selectedFolderId));
      return parentAssets.filter((a) => ids.has(a.id));
    }
    if (libraryView === 'project' && selectedProjectId) {
      return parentAssets.filter((a) => a.project_id === selectedProjectId);
    }
    if (libraryView === 'recent') {
      return [...parentAssets].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 30);
    }
    return parentAssets; // 'all' / 'archived' (archive filter applied below) / 'backups' (assets not shown there)
  }, [parentAssets, libraryView, selectedProjectId, selectedFolderId, creatorId]);

  const visibleParentAssets = useMemo(() => {
    const showArchived = libraryView === 'archived';
    let list = scopedParentAssets.filter((a) => showArchived === archivedIds.has(a.id));
    if (typeFilter !== 'all') list = list.filter((a) => a.type === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((a) => (displayNameOverrides[a.id] || a.name || '').toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sortBy === 'name') return (displayNameOverrides[a.id] || a.name || '').localeCompare(displayNameOverrides[b.id] || b.name || '');
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0); // 'newest' default
    });
    return sorted;
  }, [scopedParentAssets, libraryView, archivedIds, typeFilter, searchQuery, sortBy, displayNameOverrides]);

  // Local-only design assets (thumbnails, image posts, carousels, Stories)
  // follow the same scoping/search/sort, but never appear inside Attach
  // Existing Media — that flow is specifically for attaching video.
  const visibleLocalDesigns = useMemo(() => {
    if (attachMode) return [];
    const showArchived = libraryView === 'archived';
    let list = localDesignAssets.filter((d) => showArchived === archivedIds.has(d.id));
    if (libraryView === 'project' && selectedProjectId) list = list.filter((d) => d.projectId === selectedProjectId);
    if (libraryView === 'folder' && selectedFolderId) {
      const ids = new Set(getFolderAssetIds(creatorId, selectedFolderId));
      list = list.filter((d) => ids.has(d.id));
    }
    if (libraryView === 'recent') list = [...list].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 30);
    if (typeFilter !== 'all' && typeFilter !== 'design') return [];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((d) => (d.name || '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [localDesignAssets, attachMode, libraryView, selectedProjectId, selectedFolderId, archivedIds, typeFilter, searchQuery, sortBy, creatorId]);


  const [addVideoParentId, setAddVideoParentId] = useState(null);
  const childFileInputRef = useRef(null);
  const triggerAddVideo = (parentId) => {
    setAddVideoParentId(parentId);
    childFileInputRef.current?.click();
  };

  // ── Group existing standalone media into one Parent Asset ──
  // Only standalone Parent Assets (no children of their own yet) are
  // selectable here, to keep this stage's scope to simple grouping and
  // avoid any risk of orphaning an existing asset's own children.
  const [selectedForGrouping, setSelectedForGrouping] = useState(new Set());
  const [grouping, setGrouping] = useState(false);
  const toggleSelectForGrouping = (assetId) => {
    setSelectedForGrouping(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId); else next.add(assetId);
      return next;
    });
  };
  const handleGroupSelected = async () => {
    const ids = Array.from(selectedForGrouping);
    if (ids.length < 2) return;
    const [parentId, ...childIds] = ids;
    setGrouping(true);
    let succeeded = 0;
    let failedError = null;
    for (const childId of childIds) {
      try {
        const res = await fetch(`${API_URL}/api/admin/content-sources/${childId}/attach-parent`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ parent_asset_id: parentId }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to group asset');
        }
        succeeded += 1;
      } catch (err) {
        failedError = err;
        break; // stop on first failure, but keep whatever already succeeded
      }
    }
    // Always re-fetch from the server rather than optimistically patching
    // local state — some attach calls may have succeeded even if a later
    // one in the sequence failed, and only the server knows the true state.
    await fetchExistingAssets();
    setSelectedForGrouping(new Set());
    setGrouping(false);
    if (failedError) {
      toast.error(succeeded > 0
        ? `Grouped ${succeeded} of ${childIds.length} — ${failedError.message}`
        : (failedError.message || 'Failed to group assets'));
    } else {
      toast.success(`Grouped ${childIds.length + 1} videos into one Parent Asset`);
    }
  };

  // ── Attach existing Parent Assets to the in-context Project ──
  // Only reachable via ?mode=attach&projectId=..., i.e. the "Attach
  // Existing Media" entry point from Project Overview. Reuses the Phase 1
  // attach-project endpoint — no new storage, no new relationships.
  const [selectedForAttach, setSelectedForAttach] = useState(new Set());
  const [attaching, setAttaching] = useState(false);
  const toggleSelectForAttach = (assetId) => {
    setSelectedForAttach(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId); else next.add(assetId);
      return next;
    });
  };
  const handleAttachSelected = async () => {
    const ids = Array.from(selectedForAttach);
    if (ids.length === 0 || !projectId) return;
    setAttaching(true);
    let succeeded = 0;
    let failedError = null;
    for (const assetId of ids) {
      try {
        const res = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/attach-project`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: projectId }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to attach asset');
        }
        succeeded += 1;
      } catch (err) {
        failedError = err;
        break;
      }
    }
    await fetchExistingAssets();
    setSelectedForAttach(new Set());
    setAttaching(false);
    if (failedError) {
      toast.error(succeeded > 0
        ? `Attached ${succeeded} of ${ids.length} — ${failedError.message}`
        : (failedError.message || 'Failed to attach assets'));
    } else {
      toast.success(`Attached ${ids.length} asset${ids.length === 1 ? '' : 's'} to this project`);
    }
  };

  // ── Default thumbnail per video (stable asset id, not filename) ──
  const [thumbMap, setThumbMap] = useState({});
  const [thumbUploadingId, setThumbUploadingId] = useState(null);
  const thumbInputRef = useRef(null);
  const thumbTargetRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadThumbs = async () => {
      const results = await Promise.all(parentAssets.map(async (a) => {
        try {
          const res = await fetch(`${API_URL}/api/admin/content-sources/${a.id}/thumbnail`, { credentials: 'include' });
          if (!res.ok) return [a.id, null];
          const data = await res.json();
          return [a.id, data.thumbnail_url || null];
        } catch {
          return [a.id, null];
        }
      }));
      if (!cancelled) setThumbMap(Object.fromEntries(results));
    };
    if (parentAssets.length > 0) loadThumbs();
  }, [parentAssets]);

  const triggerSetThumbnail = (assetId) => {
    thumbTargetRef.current = assetId;
    thumbInputRef.current?.click();
  };
  const handleThumbnailFileChange = async (e) => {
    const file = e.target.files?.[0];
    const assetId = thumbTargetRef.current;
    e.target.value = '';
    if (!file || !assetId) return;
    setThumbUploadingId(assetId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to set thumbnail');
      }
      const thumbRes = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/thumbnail`, { credentials: 'include' });
      const thumbData = thumbRes.ok ? await thumbRes.json() : { thumbnail_url: null };
      setThumbMap(prev => ({ ...prev, [assetId]: thumbData.thumbnail_url || null }));
      toast.success('Thumbnail updated');
    } catch (err) {
      toast.error(err.message || 'Failed to set thumbnail');
    } finally {
      setThumbUploadingId(null);
    }
  };

  const getFormatLabel = (name) => {
    const ext = name?.split('.').pop();
    return ext && ext.length <= 5 ? ext.toUpperCase() : null;
  };

  // ── Unified grid items — real video/live/replay assets plus browser-
  // local design assets, normalized into one shape so a single card
  // component (and a single set of actions) can handle both honestly. ──
  const folderMembersMap = useMemo(() => (creatorId ? getFolderMembers(creatorId) : {}), [creatorId, folders]);
  const folderIdsForAsset = useCallback(
    (assetId) => Object.entries(folderMembersMap).filter(([, ids]) => ids.includes(assetId)).map(([fid]) => fid),
    [folderMembersMap],
  );

  const gridItems = useMemo(() => {
    const videoItems = visibleParentAssets.map((a) => ({
      id: a.id,
      kind: a.type === 'upload' ? 'video' : a.type,
      assetKind: a.type,
      displayName: displayNameOverrides[a.id] || a.name,
      thumbnailUrl: thumbMap[a.id] || null,
      projectName: projects.find((p) => (p.id || p._id) === a.project_id)?.name || null,
      isArchived: archivedIds.has(a.id),
      localOnly: false,
      childCount: (childrenByParent[a.id] || []).length,
      statusBadge: getStatusBadge(a),
      _raw: { ...a, name: displayNameOverrides[a.id] || a.name },
    }));
    const designItems = visibleLocalDesigns.map((d) => ({
      id: d.id,
      kind: 'design',
      assetKind: d.assetKind,
      displayName: d.name,
      thumbnailUrl: d.thumbnailUrl,
      projectName: d.projectName,
      isArchived: archivedIds.has(d.id),
      localOnly: true,
      childCount: 0,
      statusBadge: null,
      _raw: d,
    }));
    return [...videoItems, ...designItems];
  }, [visibleParentAssets, visibleLocalDesigns, displayNameOverrides, thumbMap, projects, archivedIds, childrenByParent]);


  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="mb-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-[#8A93A6] tracking-wide mb-4" data-testid="media-library-breadcrumb">
            <Link to="/app/create/hub" className="hover:text-white transition-colors">Create</Link>
            <span className="opacity-50">/</span>
            {projectId && (
              <>
                <Link to="/app/create/workspace" className="hover:text-white transition-colors">Workspace</Link>
                <span className="opacity-50">/</span>
                <Link to={`/app/create/workspace/project/${projectId}`} className="hover:text-white transition-colors" data-testid="media-library-back-to-project-link">Project</Link>
                <span className="opacity-50">/</span>
              </>
            )}
            <span className="text-[#C0C7D4]">Media Library</span>
          </div>

          {/* Page Title */}
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h1 className="text-3xl font-bold text-white" data-testid="media-library-title">
              {attachMode ? 'Attach Existing Media' : 'Media Library'}
            </h1>
            <div className="flex items-center gap-2">
              <ProductionToolsDropdown currentTool="media-library" creatorId={creatorId} projectId={projectId || selectedProjectId} returnTo={returnTo} />
              {returnTo && (
                <Link to={returnTo} className="inline-flex items-center gap-1.5 text-xs text-[#8A93A6] hover:text-white" data-testid="media-library-return-link">
                  <ArrowLeft className="h-3.5 w-3.5" />Back
                </Link>
              )}
            </div>
          </div>

          {/* Creator Context */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
              <User className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white uppercase tracking-wide" data-testid="active-creator-name">
                {creatorInfo?.name || creatorInfo?.display_name || 'Creator'}
              </p>
              {(creatorInfo?.handle || creatorInfo?.username) && (
                <p className="text-xs text-[#8A93A6]">
                  {creatorInfo.handle || creatorInfo.username}
                </p>
              )}
            </div>
          </div>

          {/* Storage notice — Backblaze B2 is the real primary storage for
              every upload; this only ever reports the optional Dropbox
              backup connection, never a substitute for primary storage. */}
          <div className="text-xs text-brand-text-muted bg-white/5 border border-white/10 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="storage-notice">
            <span className="font-semibold text-white">Storage:</span>
            <span>{storageNotice}</span>
            <button onClick={() => setLibraryView('backups')} className="text-[#3A7BFF] hover:text-white ml-auto" data-testid="storage-notice-manage-backups-link">Manage backups →</button>
          </div>
        </div>

        <div className="flex gap-6">
          <MediaLibrarySidebar
            creatorName={creatorInfo?.name || creatorInfo?.display_name || 'Creator'}
            view={libraryView}
            onViewChange={(v) => setLibraryView(v)}
            projects={projects}
            selectedProjectId={selectedProjectId}
            onSelectProject={(id) => { setSelectedProjectId(id); setLibraryView('project'); }}
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={(id) => { setSelectedFolderId(id); setLibraryView('folder'); }}
            onCreateFolder={() => setCreateFolderOpen(true)}
            archivedCount={archivedIds.size}
            archivedProjectIds={archivedProjectIds}
            archivedFolderIds={archivedFolderIds}
            onArchiveProject={handleArchiveProject}
            onArchiveFolder={handleArchiveFolder}
          />

          <div className="flex-1 min-w-0">
            {libraryView === 'backups' ? (
              <Card className="glass">
                <CardHeader><CardTitle>Backups</CardTitle></CardHeader>
                <CardContent>
                  <BackupSettingsPanel creatorId={creatorId} projects={projects} dropboxConnected={dropboxConnected} />
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="glass mb-6">
                  <CardContent className="pt-6">
                    <input ref={fileInputRef} type="file" accept="video/*" className="hidden" multiple onChange={e => handleUploadFiles(e.target.files)} />
                    <input ref={childFileInputRef} type="file" accept="video/*" className="hidden" onChange={e => { handleUploadFiles(e.target.files, addVideoParentId); e.target.value = ''; }} />
                    <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbnailFileChange} />
                    <div
                      role="button" tabIndex={0}
                      onClick={() => !isUploading && fileInputRef.current?.click()}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                      onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                      onDrop={e => { e.preventDefault(); setIsDragging(false); handleUploadFiles(e.dataTransfer.files); }}
                      className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${isDragging ? 'border-brand-blue bg-brand-blue/10' : 'border-white/20 hover:border-brand-blue'} ${isUploading ? 'opacity-60 cursor-wait' : ''}`}
                      data-testid="upload-dropzone"
                    >
                      <UploadIcon className="h-12 w-12 text-brand-text-muted mx-auto mb-4" />
                      <p className="text-white font-medium mb-2">Drop files here or click to browse</p>
                      <p className="text-sm text-brand-text-muted">Supports video files up to 5GB. Each upload becomes a new Parent Asset.</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Toolbar — search, type, sort, view mode */}
                <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="media-library-toolbar">
                  <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="h-3.5 w-3.5 text-[#555E6E] absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search media…"
                      className="h-8 text-xs bg-black/40 border-white/10 pl-8"
                      data-testid="media-library-search-input"
                    />
                  </div>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 w-32" data-testid="media-library-type-filter"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1C1F26] border-white/10">
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="upload">Video</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="replay">Replay</SelectItem>
                      {!attachMode && <SelectItem value="design">Images &amp; Posts</SelectItem>}
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 w-32" data-testid="media-library-sort-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1C1F26] border-white/10">
                      <SelectItem value="newest">Newest first</SelectItem>
                      <SelectItem value="oldest">Oldest first</SelectItem>
                      <SelectItem value="name">Name (A–Z)</SelectItem>
                    </SelectContent>
                  </Select>
                  {!attachMode && (
                    <div className="flex items-center rounded-lg border border-white/10 overflow-hidden">
                      <button onClick={() => setViewMode('grid')} className={`p-1.5 ${viewMode === 'grid' ? 'bg-[#3A7BFF]/20 text-white' : 'text-[#8A93A6]'}`} title="Grid view" data-testid="media-library-view-grid-btn"><LayoutGrid className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setViewMode('list')} className={`p-1.5 ${viewMode === 'list' ? 'bg-[#3A7BFF]/20 text-white' : 'text-[#8A93A6]'}`} title="List view" data-testid="media-library-view-list-btn"><ListIcon className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>

                <Card className="glass">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>
                      {libraryView === 'archived' ? 'Archived' : libraryView === 'recent' ? 'Recent' : libraryView === 'folder' ? (folders.find(f => f.id === selectedFolderId)?.name || 'Folder') : libraryView === 'project' ? (projects.find(p => p.id === selectedProjectId)?.name || 'Project') : 'All Media'}
                    </CardTitle>
                    {attachMode && selectedForAttach.size > 0 && (
                      <Button size="sm" onClick={handleAttachSelected} disabled={attaching} data-testid="attach-selected-btn">
                        {attaching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                        Attach {selectedForAttach.size} to Project
                      </Button>
                    )}
                    {!attachMode && selectedForGrouping.size >= 2 && (
                      <Button size="sm" onClick={handleGroupSelected} disabled={grouping} data-testid="group-selected-btn">
                        {grouping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                        Group {selectedForGrouping.size} into One Asset
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {libraryView === 'archived' && (archivedProjectIds.size > 0 || archivedFolderIds.size > 0) && (
                      <div className="mb-4 space-y-2" data-testid="archived-projects-folders">
                        {projects.filter((p) => archivedProjectIds.has(p.id)).map((p) => (
                          <div key={p.id} className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                            <span className="text-xs text-[#C0C7D4] flex items-center gap-1.5"><FolderOpen className="h-3.5 w-3.5" />{p.name} <span className="text-[#555E6E]">(project)</span></span>
                            <button onClick={() => handleArchiveProject(p.id, false)} className="text-xs text-[#3A7BFF] hover:text-white" data-testid={`restore-project-${p.id}`}>Restore</button>
                          </div>
                        ))}
                        {folders.filter((f) => archivedFolderIds.has(f.id)).map((f) => (
                          <div key={f.id} className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                            <span className="text-xs text-[#C0C7D4] flex items-center gap-1.5"><FolderOpen className="h-3.5 w-3.5" />{f.name} <span className="text-[#555E6E]">(folder)</span></span>
                            <button onClick={() => handleArchiveFolder(f.id, false)} className="text-xs text-[#3A7BFF] hover:text-white" data-testid={`restore-folder-${f.id}`}>Restore</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {visibleParentAssets.length === 0 && visibleLocalDesigns.length === 0 && (
                      <p className="text-sm text-brand-text-muted text-center py-6" data-testid="media-library-empty-state">
                        {libraryView === 'archived' ? 'Nothing archived here yet.' : libraryView === 'project' && !attachMode ? "No media in this project yet. Upload above, or attach something already in this Creator's library from a project page." : 'No media yet. Upload a file above to get started.'}
                      </p>
                    )}

                    {(attachMode || viewMode === 'list') ? (
                      <div className="space-y-3">
                        {visibleParentAssets.map(asset => {
                          const isUploading = asset.status === 'uploading' || asset.status === 'finalizing';
                          const children = childrenByParent[asset.id] || [];
                          const canEdit = !isUploading && asset.status !== 'failed' && asset.status !== 'finalize_failed';
                          const canSelectForGrouping = canEdit && children.length === 0;
                          const alreadyInOtherProject = !!asset.project_id && asset.project_id !== projectId;
                          const canSelectForAttach = attachMode && canEdit && !alreadyInOtherProject && asset.project_id !== projectId;
                          const formatLabel = getFormatLabel(asset.name);
                          const thumbUrl = thumbMap[asset.id];
                          const displayName = displayNameOverrides[asset.id] || asset.name;

                          const borderClass =
                            asset.status === 'uploading' ? 'border-blue-500/40' :
                            asset.status === 'finalizing' ? 'border-purple-500/40' :
                            (asset.status === 'finalize_failed' || asset.status === 'failed') ? 'border-red-500/40' : '';

                          return (
                            <div key={asset.id} className={`rounded-lg bg-white/5 border border-white/10 overflow-hidden transition-all ${borderClass}`} data-testid={`asset-row-${asset.id}`}>
                              <div className="flex items-center gap-4 p-4">
                                {canSelectForAttach && (
                                  <Checkbox
                                    checked={selectedForAttach.has(asset.id)}
                                    onCheckedChange={() => toggleSelectForAttach(asset.id)}
                                    data-testid={`select-attach-${asset.id}`}
                                  />
                                )}
                                {attachMode && alreadyInOtherProject && (
                                  <span className="text-[10px] text-[#8A93A6] shrink-0" title="Already attached to another project" data-testid={`attach-unavailable-${asset.id}`}>
                                    In another project
                                  </span>
                                )}
                                {!attachMode && canSelectForGrouping && (
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <Checkbox
                                      checked={selectedForGrouping.has(asset.id)}
                                      onCheckedChange={() => toggleSelectForGrouping(asset.id)}
                                      data-testid={`select-group-${asset.id}`}
                                    />
                                    {selectedForGrouping.size >= 2 && Array.from(selectedForGrouping)[0] === asset.id && (
                                      <Badge className="bg-brand-blue/20 text-brand-blue border-brand-blue/30 text-[10px] px-1.5 py-0" data-testid={`becomes-parent-${asset.id}`}>
                                        Becomes Parent
                                      </Badge>
                                    )}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="w-10 h-10 rounded overflow-hidden bg-black/30 flex items-center justify-center shrink-0"
                                  onClick={() => setPreviewAsset({ ...asset, name: displayName, projectName: projects.find(p => p.id === asset.project_id)?.name || null })}
                                  data-testid={`asset-thumbnail-${asset.id}`}
                                >
                                  {thumbUrl ? (
                                    <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className={isUploading ? 'animate-pulse' : ''}>
                                      <Video className={`h-5 w-5 ${
                                        asset.status === 'uploading' ? 'text-blue-400' :
                                        asset.status === 'finalizing' ? 'text-purple-400' :
                                        'text-brand-blue'
                                      }`} />
                                    </div>
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-white font-medium truncate">{displayName}</p>
                                    {asset.type !== 'upload' && (
                                      <Badge variant="outline" className="border-white/20 text-[#8A93A6] text-xs flex items-center gap-1"><Radio className="h-3 w-3" />{asset.type === 'live' ? 'Live' : 'Replay'}</Badge>
                                    )}
                                    {formatLabel && (
                                      <Badge variant="outline" className="border-white/20 text-[#8A93A6] text-xs" data-testid={`format-badge-${asset.id}`}>{formatLabel}</Badge>
                                    )}
                                    {children.length > 0 && (
                                      <Badge variant="outline" className="border-white/20 text-[#8A93A6] text-xs flex items-center gap-1" data-testid={`parent-child-count-${asset.id}`}>
                                        <Layers className="h-3 w-3" />
                                        {children.filter(c => c.status !== 'failed' && c.status !== 'finalize_failed').length + 1} videos
                                      </Badge>
                                    )}
                                    {archivedIds.has(asset.id) && (
                                      <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-xs">Archived</Badge>
                                    )}
                                  </div>
                                  {isUploading && asset.fileSize && (
                                    <p className="text-xs text-brand-text-muted mt-0.5">{formatFileSize(asset.fileSize)}</p>
                                  )}
                                  {(asset.status === 'finalize_failed' || asset.status === 'failed') && asset.errorMessage && (
                                    <p className="text-xs text-red-400 mt-0.5 truncate">{asset.errorMessage}</p>
                                  )}
                                </div>

                                {getStatusBadge(asset)}

                                {asset.status === 'finalize_failed' && (
                                  <Button size="sm" variant="outline" onClick={() => retryFinalize(asset)} className="flex items-center gap-1 border-purple-500/30 text-purple-400 hover:bg-purple-500/10" data-testid={`retry-finalize-${asset.id}`}>
                                    <Loader2 className="h-4 w-4" />Retry
                                  </Button>
                                )}

                                {/* Parent Asset action dropdown — Parent Assets only, never on Child Assets */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="icon" variant="outline" className="border-white/10 h-8 w-8 shrink-0" data-testid={`asset-actions-menu-${asset.id}`}>
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem disabled={!canEdit} onClick={() => navigate(`/app/create/editor/${asset.id}`)} data-testid={`asset-action-edit-${asset.id}`}>
                                      <Pencil className="h-4 w-4 mr-2" />Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem disabled={!canEdit} onClick={() => triggerAddVideo(asset.id)} data-testid={`asset-action-addvideo-${asset.id}`}>
                                      <PlusCircle className="h-4 w-4 mr-2" />Add Video
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(asset.project_id ? `/app/create/optimize?assetId=${asset.id}&projectId=${asset.project_id}&creatorId=${creatorId || ''}` : '/app/create/schedule')} data-testid={`asset-action-schedule-${asset.id}`}>
                                      <CalendarClock className="h-4 w-4 mr-2" />Optimize &amp; Schedule
                                    </DropdownMenuItem>
                                    <DropdownMenuItem disabled={thumbUploadingId === asset.id} onClick={() => triggerSetThumbnail(asset.id)} data-testid={`asset-action-thumbnail-${asset.id}`}>
                                      {thumbUploadingId === asset.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                                      {thumbUrl ? 'Change Thumbnail' : 'Set Thumbnail'}
                                    </DropdownMenuItem>
                                    {!attachMode && (
                                      <DropdownMenuItem onClick={() => { const next = window.prompt('Rename', displayName); if (next && next.trim()) handleRenameAsset(asset, next.trim()); }} data-testid={`asset-action-rename-${asset.id}`}>
                                        <Pencil className="h-4 w-4 mr-2" />Rename (local label)
                                      </DropdownMenuItem>
                                    )}
                                    {!attachMode && (
                                      <DropdownMenuItem onClick={() => handleArchiveToggle(asset, !archivedIds.has(asset.id))} data-testid={`asset-action-archive-${asset.id}`}>
                                        {archivedIds.has(asset.id) ? <ArchiveRestore className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
                                        {archivedIds.has(asset.id) ? 'Restore' : 'Archive'}
                                      </DropdownMenuItem>
                                    )}
                                    {!attachMode && (
                                      <DropdownMenuItem onClick={() => { setBackupSelection([asset]); setBackupDialogOpen(true); }} data-testid={`asset-action-backup-${asset.id}`}>
                                        <Cloud className="h-4 w-4 mr-2" />Back Up to Dropbox
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="text-red-400 focus:text-red-400" onClick={() => handleRemoveAsset({ ...asset, name: displayName })} data-testid={`asset-action-delete-${asset.id}`}>
                                      <Trash2 className="h-4 w-4 mr-2" />Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>

                              {asset.status === 'uploading' && (
                                <div className="w-full bg-white/5 h-1.5">
                                  <div className="bg-blue-500 h-1.5 transition-all duration-300 ease-out" style={{ width: `${asset.progress || 0}%` }} />
                                </div>
                              )}
                              {asset.status === 'finalizing' && (
                                <div className="w-full bg-white/5 h-1.5 overflow-hidden">
                                  <div className="bg-purple-500 h-1.5 w-1/3 animate-[finalize-slide_1.5s_ease-in-out_infinite]" />
                                </div>
                              )}

                              {/* Child Assets — same project, no action dropdown of their own */}
                              {children.length > 0 && (
                                <div className="border-t border-white/10 bg-black/20 px-4 py-2 space-y-2" data-testid={`parent-children-${asset.id}`}>
                                  {children.map(child => (
                                    <div key={child.id} className="flex items-center gap-3 pl-6" data-testid={`child-asset-row-${child.id}`}>
                                      <Video className="h-4 w-4 text-brand-text-muted shrink-0" />
                                      <p className="text-sm text-[#C0C7D4] truncate flex-1">{child.name}</p>
                                      {getStatusBadge(child)}
                                      {child.status === 'uploading' && (
                                        <span className="text-xs text-brand-text-muted">{child.progress || 0}%</span>
                                      )}
                                      {(child.status === 'failed' || child.status === 'finalize_failed') && (
                                        <button onClick={() => handleRemoveAsset(child)} className="text-red-400 hover:text-red-300" data-testid={`dismiss-child-${child.id}`}>
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-library-grid">
                        {gridItems.map((item) => (
                          <AssetGridCard
                            key={item.id}
                            item={item}
                            folders={folders}
                            folderIds={folderIdsForAsset(item.id)}
                            onPreview={(it) => setPreviewAsset({ ...it._raw, projectName: it.projectName })}
                            onRename={(it, name) => handleRenameAsset(it._raw, name)}
                            onArchiveToggle={(it, archived) => handleArchiveToggle(it._raw, archived)}
                            onDelete={(it) => handleRemoveAsset(it._raw)}
                            onEdit={!item.localOnly ? (it) => navigate(`/app/create/editor/${it._raw.id}`) : undefined}
                            onBackup={!item.localOnly ? (it) => { setBackupSelection([it._raw]); setBackupDialogOpen(true); } : undefined}
                            onToggleFolder={(it, folderId, inFolder) => handleToggleFolderMembership(it._raw, folderId, inFolder)}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>

      <AssetPreviewDialog asset={previewAsset} open={!!previewAsset} onOpenChange={(v) => !v && setPreviewAsset(null)} apiUrl={API_URL} />
      <DeleteAssetDialog
        asset={deleteTarget}
        creatorId={creatorId}
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onConfirm={() => deleteTarget && performDeleteAsset(deleteTarget)}
      />
      <DropboxBackupDialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen} selection={backupSelection} dropboxConnected={dropboxConnected} />
      <CreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        onCreate={({ name }) => handleCreateFolder(name)}
        projects={projects}
        defaultProjectId={libraryView === 'project' ? selectedProjectId : null}
      />
    </div>
  );
}
